import type { CredentialStatus, CredentialVerifyResponse } from '@et/shared';
import { useState } from 'react';
import { api, describeError } from '../api/client';
import { ConfirmDialog } from '../components/Dialog';
import { dateTime } from '../lib/format';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';

const HELP: Record<string, string> = {
  binance: 'Spot trading permission only — NO withdrawal permission — IP-restricted to the VPS. Needed only for live mode.',
  telegram: 'Bot token from @BotFather. Commands are accepted only from TELEGRAM_ALLOWED_CHAT_ID.',
  claude: 'Optional and paid per call. The bot runs on the free regex engine without it.',
  openai: 'Optional and paid per call. The bot runs on the free regex engine without it.',
};

function CredentialRow({ c, onChanged }: { c: CredentialStatus; onChanged: () => void }): JSX.Element {
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const tr = useT();

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card stack">
      <div className="row">
        <h2 style={{ margin: 0 }}>{c.provider}</h2>
        {c.isSet ? <span className="chip good"><span className="dot" /> {tr('set')} {c.maskedHint}</span> : <span className="chip">{tr('not set')}</span>}
        {c.lastVerifiedAt && <span className="muted">{tr('verified {time}', { time: dateTime(c.lastVerifiedAt) })}</span>}
      </div>
      <div className="muted">{tr(HELP[c.provider] ?? '')}</div>
      <div className="row">
        <input type="password" placeholder={c.provider === 'telegram' ? tr('bot token') : tr('API key')} value={apiKey} onChange={(e) => { setApiKey(e.target.value); }} autoComplete="off" />
        {c.provider === 'binance' && <input type="password" placeholder={tr('secret')} value={secret} onChange={(e) => { setSecret(e.target.value); }} autoComplete="off" />}
        <button className="primary" disabled={busy || !apiKey} onClick={() => void run(async () => {
          await api.put(`/credentials/${c.provider}`, { apiKey, ...(secret ? { secret } : {}) });
          setApiKey('');
          setSecret('');
          toast.success(t('{provider} saved (encrypted)', { provider: c.provider }));
          onChanged();
        })}>{tr('Save')}</button>
        {c.isSet && <button disabled={busy} onClick={() => void run(async () => {
          const r = await api.post<CredentialVerifyResponse>(`/credentials/${c.provider}/verify`);
          if (r.ok) toast.success(r.detail);
          else toast.error(t('Verify failed: {detail}', { detail: r.detail }));
          onChanged();
        })}>{tr('Verify')}</button>}
        {c.isSet && <button disabled={busy} onClick={() => { setConfirmRemove(true); }}>{tr('Remove')}</button>}
      </div>
      {confirmRemove && (
        <ConfirmDialog title={tr('Remove the {provider} credential?', { provider: c.provider })} confirmLabel={tr('Remove')} danger onCancel={() => { setConfirmRemove(false); }} onConfirm={() => void run(async () => {
          await api.del(`/credentials/${c.provider}`);
          setConfirmRemove(false);
          toast.success(t('Removed'));
          onChanged();
        })}>{tr('Anything depending on it stops working until a new one is saved.')}</ConfirmDialog>
      )}
    </div>
  );
}

export function Credentials(): JSX.Element {
  const creds = useResource<CredentialStatus[]>('/credentials');
  const tr = useT();
  return (
    <div className="stack">
      <h1>{tr('Credentials')}</h1>
      <div className="muted">{tr('Stored AES-256-GCM encrypted. The server never sends a key back — only its last four characters.')}</div>
      {creds.data?.map((c) => <CredentialRow key={c.provider} c={c} onChanged={() => void creds.reload()} />)}
    </div>
  );
}
